import type { ApiResponse } from '@neuroforge/shared'

import { api, ApiError } from './api-client'
import { safeRedirectTarget } from './safe-redirect'
import { currentSessionSnapshot, isSessionSnapshotCurrent, refreshSession } from './session-refresh'
import { useAuthStore } from './stores/auth.store'
import type { AuthUser } from './stores/auth.store'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:6300/api/v1'
const HUB_URL = import.meta.env.VITE_HUB_URL || 'http://localhost:6312'

let callbackInFlight: { token: string; promise: Promise<boolean> } | null = null

function normalizeUrl(url: string): string {
  return url.replace(/\/$/, '')
}

function currentReturnUrl(): string {
  return `${window.location.origin}${window.location.pathname}${window.location.search}`
}

/**
 * Lit les paramètres du handoff OAuth depuis le FRAGMENT
 * (`#token=…&redirect=…` côté Hub, `#nf_token=…` côté sous-app).
 *
 * Le fragment n'est jamais transmis au serveur : il n'apparaît ni dans les
 * access logs nginx, ni dans l'en-tête Referer des requêtes sortantes —
 * contrairement à une query string.
 *
 * La lecture est volontairement NON destructive : on ne purge la barre
 * d'adresse qu'une fois la session réellement établie, via `clearAuthFragment()`.
 * Purger dès la lecture rend le callback non rejouable (double montage
 * <StrictMode> en dev, retour arrière, re-render) et fait échouer la seconde
 * passe alors que la session est valide.
 *
 * Source unique de vérité pour la lecture du fragment : le Hub
 * (`apps/hub/src/pages/AuthCallbackPage.tsx`) consomme ce helper au lieu
 * d'en garder une copie locale.
 */
export function readAuthFragmentParams(): URLSearchParams {
  if (typeof window === 'undefined') return new URLSearchParams()

  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash
  return new URLSearchParams(hash)
}

function readFragmentToken(): string | null {
  const params = readAuthFragmentParams()
  return params.get('nf_token') || params.get('token')
}

/** Purge le fragment d'auth de la barre d'adresse. À n'appeler qu'APRÈS succès. */
export function clearAuthFragment(): void {
  if (typeof window === 'undefined' || !window.location.hash) return
  // Preserve any non-auth anchor on deep links (e.g. #section&nf_token=...).
  const kept = window.location.hash
    .slice(1)
    .split('&')
    .filter((part) => !/^(?:nf_token|token|redirect|state)=/.test(part))
  if (kept.length === 1 && kept[0] === window.location.hash.slice(1)) return
  const suffix = kept.filter(Boolean).join('&')
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}${suffix ? `#${suffix}` : ''}`,
  )
}

/** Route dédiée au callback OAuth, montée par le Hub et par chaque sous-app. */
const AUTH_CALLBACK_PATH = '/auth/callback'

/**
 * Vrai si la page courante est la route de callback.
 *
 * Sur cette route, la page dédiée est PROPRIÉTAIRE du fragment : le bootstrap
 * ne doit surtout pas le consommer à sa place (cf. `tryAutoRefresh`).
 */
export function isOnAuthCallbackRoute(): boolean {
  if (typeof window === 'undefined') return false
  return window.location.pathname.replace(/\/+$/, '') === AUTH_CALLBACK_PATH
}

/** Redirige vers Discord OAuth */
export function loginWithDiscord(returnUrl?: string) {
  const state = safeRedirectTarget(returnUrl || currentReturnUrl()).toString()
  window.location.href = `${API_URL}/auth/discord?state=${encodeURIComponent(state)}`
}

/** Redirige vers Google OAuth */
export function loginWithGoogle(returnUrl?: string) {
  const state = safeRedirectTarget(returnUrl || currentReturnUrl()).toString()
  window.location.href = `${API_URL}/auth/google?state=${encodeURIComponent(state)}`
}

/** Only allowlisted, credential-free return URLs may be echoed into Hub queries. */
function absoluteReturnUrl(returnUrl?: string): string {
  return safeRedirectTarget(returnUrl || currentReturnUrl()).toString()
}

/** URL absolue du login centralisé Hub, avec retour vers l'app courante après connexion. */
export function hubLoginUrl(returnUrl?: string): string {
  return `${normalizeUrl(HUB_URL)}/auth/login?redirect=${encodeURIComponent(absoluteReturnUrl(returnUrl))}`
}

/** URL absolue de l'inscription centralisée Hub. */
export function hubRegisterUrl(returnUrl?: string): string {
  return `${normalizeUrl(HUB_URL)}/auth/register?redirect=${encodeURIComponent(absoluteReturnUrl(returnUrl))}`
}

/** Hub email login -> same OAuth callback contract; JWT only in URL fragment. */
export function hubEmailCallbackUrl(accessToken: string, rawRedirect: string): string {
  if (!accessToken) throw new AuthSessionError()
  const target = safeRedirectTarget(rawRedirect)
  const callback = new URL('/auth/callback', window.location.origin)
  callback.hash = new URLSearchParams({
    token: accessToken,
    redirect: target.toString(),
  }).toString()
  return callback.toString()
}

/** Redirige vers le login centralisé Hub */
export function redirectToHubLogin(returnUrl?: string) {
  window.location.href = hubLoginUrl(returnUrl)
}

/** Redirige vers l'inscription centralisée Hub */
export function redirectToHubRegister(returnUrl?: string) {
  window.location.href = hubRegisterUrl(returnUrl)
}

/** Recoverable auth error displayed by the sub-app callback page. */
export class AuthSessionError extends Error {
  constructor(message = 'Session invalide ou expirée. Merci de vous reconnecter.') {
    super(message)
    this.name = 'AuthSessionError'
  }
}

/** Handles #nf_token / #token; never accepts JWTs from URL query strings. */
export function handleAuthCallback(_searchParams?: URLSearchParams): Promise<boolean> {
  const token = readFragmentToken()
  if (!token) return Promise.resolve(false)
  if (callbackInFlight?.token === token) return callbackInFlight.promise

  // Block bootstrap/401 cookie refresh while the handoff is being verified.
  useAuthStore.getState().setAuthPending(true)
  useAuthStore.getState().setAccessToken(token)
  const context = currentSessionSnapshot()

  const promise = (async (): Promise<boolean> => {
    let completed = false
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
        referrerPolicy: 'no-referrer',
      })
      if (response.status === 401 || response.status === 403) throw new AuthSessionError()
      if (!response.ok) return false

      const payload = (await response.json()) as { success?: boolean; data?: AuthUser }
      if (!payload.success || !payload.data?.id || !isSessionSnapshotCurrent(context)) return false

      // W1 establish-session demands an already server-validated access JWT
      // carrying sid. Never fabricate sid or silently ignore a 401/403.
      const established = await fetch(`${API_URL}/auth/establish-session`, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
        referrerPolicy: 'no-referrer',
      })
      if (established.status === 401 || established.status === 403) throw new AuthSessionError()
      if (!established.ok) return false
      const session = (await established.json()) as {
        success?: boolean
        data?: { accessToken?: string }
      }
      if (!session.success || !session.data?.accessToken || !isSessionSnapshotCurrent(context)) {
        return false
      }

      useAuthStore.getState().setAccessToken(session.data.accessToken)
      useAuthStore.getState().setUser(payload.data)
      // Clear only the fragment we actually consumed and only after both
      // authentication and local cookie establishment succeeded.
      if (readFragmentToken() === token) clearAuthFragment()
      completed = true
      return true
    } catch (error) {
      if (error instanceof AuthSessionError) throw error
      return false
    } finally {
      // A late response must never revive an account removed by logout/switch.
      if (!completed && isSessionSnapshotCurrent(context)) useAuthStore.getState().logout()
    }
  })()

  const shared = promise.finally(() => {
    if (callbackInFlight?.promise === shared) callbackInFlight = null
  })
  callbackInFlight = { token, promise: shared }
  return shared
}

/** Clear the UI session synchronously BEFORE waiting on the server response. */
export async function logout(): Promise<void> {
  const token = useAuthStore.getState().accessToken
  useAuthStore.getState().logout()
  try {
    await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      cache: 'no-store',
    })
  } catch {
    // Local revocation is already effective; do not restore stale state.
  }
}

// ═══════════════════════════════════════════════════════════
// EMAIL AUTH HELPERS
// ═══════════════════════════════════════════════════════════

interface RegisterPayload {
  email: string
  password: string
  displayName: string
  username?: string
  capToken: string
}

interface LoginEmailPayload {
  email: string
  password: string
  capToken: string
}

/** Inscription par email + mot de passe. */
export async function registerWithEmail(payload: RegisterPayload): Promise<{ message: string }> {
  const response = await api.post<ApiResponse<{ message: string }>>('/auth/register', payload)
  return response.data!
}

/** Connexion par email + mot de passe. Stocke le token et fetch /auth/me. */
export async function loginWithEmail(payload: LoginEmailPayload): Promise<void> {
  const response = await api.post<ApiResponse<{ accessToken: string }>>('/auth/login', payload)
  const { accessToken } = response.data!

  useAuthStore.getState().setAuthPending(true)
  useAuthStore.getState().setAccessToken(accessToken)
  try {
    await fetchCurrentUser()
  } catch (error) {
    throw error instanceof AuthSessionError ? error : new AuthSessionError()
  }
}

/** Demande de réinitialisation de mot de passe (anti-énumération côté API). */
export async function forgotPassword(email: string): Promise<{ message: string }> {
  const response = await api.post<ApiResponse<{ message: string }>>('/auth/forgot-password', {
    email,
  })
  return response.data!
}

/** Réinitialise le mot de passe via un token reçu par email. */
export async function resetPassword(token: string, password: string): Promise<{ message: string }> {
  const response = await api.post<ApiResponse<{ message: string }>>('/auth/reset-password', {
    token,
    password,
  })
  return response.data!
}

/** Vérifie l'email via un token reçu par email. */
export async function verifyEmail(token: string): Promise<{ message: string }> {
  const response = await api.post<ApiResponse<{ message: string }>>('/auth/verify-email', { token })
  return response.data!
}

/** Renvoie l'email de vérification. */
export async function resendVerification(email: string): Promise<{ message: string }> {
  const response = await api.post<ApiResponse<{ message: string }>>('/auth/resend-verification', {
    email,
  })
  return response.data!
}

/** Commit /auth/me only for the token/session that initiated the request. */
export async function fetchCurrentUser(): Promise<void> {
  const context = currentSessionSnapshot()
  try {
    const response = await api.get<ApiResponse<AuthUser>>('/auth/me')
    if (!isSessionSnapshotCurrent(context)) throw new AuthSessionError()
    if (!response.success || !response.data?.id) throw new AuthSessionError()
    useAuthStore.getState().setUser(response.data)
  } catch (error) {
    if (
      isSessionSnapshotCurrent(context) &&
      !(error instanceof ApiError && error.code === 'REFRESH_UNAVAILABLE')
    ) {
      useAuthStore.getState().logout()
    }
    throw error
  }
}

/** Bootstrap must leave /auth/callback's fragment to its dedicated page. */
export async function tryAutoRefresh(): Promise<boolean> {
  if (useAuthStore.getState().authPending) return false
  if (useAuthStore.getState().isAuthenticated && useAuthStore.getState().accessToken) {
    return true
  }
  if (isOnAuthCallbackRoute()) return false

  if (readFragmentToken()) {
    try {
      return await handleAuthCallback()
    } catch {
      return false
    }
  }

  const refreshed = await refreshSession()
  if (!refreshed) return false
  if (useAuthStore.getState().isAuthenticated) return true
  try {
    await fetchCurrentUser()
    return useAuthStore.getState().isAuthenticated
  } catch {
    return false
  }
}
